import { useEffect, useState } from 'react';
import { Sheet } from './Sheet';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import type { HeatBand } from '@/modules/heat/types';

export const ROOMS = ['all', 'east', 'north', 'central', 'west', 'south'] as const;
export type Room = (typeof ROOMS)[number];

export const PRESENCES = ['any', 'recent', 'live'] as const;
export type PresenceFilter = (typeof PRESENCES)[number];

const BANDS: HeatBand[] = ['cool', 'warm', 'hot', 'pulse'];

export interface FeedFilters {
  room: Room;
  distanceKm: number;
  presence: PresenceFilter;
  bands: HeatBand[];
  /** Free-text search over handle / location / mood / interests. */
  query: string;
}

export const DEFAULT_FILTERS: FeedFilters = {
  room: 'all',
  distanceKm: 25,
  presence: 'any',
  bands: ['cool', 'warm', 'hot', 'pulse'],
  query: '',
};

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  value: FeedFilters;
  onApply: (next: FeedFilters) => void;
}

/**
 * Feed filter sheet — room (segmented), distance (slider), presence
 * (segmented), HEAT band (multi-select). Apply / Reset in the footer.
 *
 * Local working state copies `value` on open; only commits on Apply.
 */
export const FilterSheet = ({ open, onClose, value, onApply }: FilterSheetProps) => {
  const [draft, setDraft] = useState<FeedFilters>(value);

  // Reset draft when reopened with new value
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const setQuery = (query: string) => setDraft((d) => ({ ...d, query }));
  const setRoom = (room: Room) => setDraft((d) => ({ ...d, room }));
  const setDistance = (distanceKm: number) => setDraft((d) => ({ ...d, distanceKm }));
  const setPresence = (presence: PresenceFilter) => setDraft((d) => ({ ...d, presence }));
  const toggleBand = (band: HeatBand) =>
    setDraft((d) => ({
      ...d,
      bands: d.bands.includes(band)
        ? d.bands.filter((b) => b !== band)
        : [...d.bands, band],
    }));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Filter the room"
      title={
        <>
          The shape of tonight<span className="text-pink">.</span>
        </>
      }
      footer={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDraft(DEFAULT_FILTERS)}
            className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted hover:text-pink"
          >
            Reset
          </button>
          <span aria-hidden className="ml-auto" />
          <NeonButton
            variant="outline"
            size="md"
            onClick={onClose}
          >
            Cancel
          </NeonButton>
          <NeonButton
            size="md"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply
          </NeonButton>
        </div>
      }
    >
      <div className="space-y-7">
        {/* Search */}
        <FilterSection title="Search">
          <label className="flex items-center gap-3 border border-white/[0.12] bg-ink/40 px-3 py-2.5">
            <Icon name="search" size={16} className="text-text-muted" />
            <input
              type="search"
              value={draft.query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Handle, suburb, mood, interest…"
              className={cn(
                'w-full bg-transparent',
                'font-display text-sm tracking-tight text-text-primary',
                'placeholder:text-text-muted focus:outline-none',
              )}
            />
            {draft.query.length > 0 && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-text-muted hover:text-pink"
              >
                <Icon name="close" size={13} />
              </button>
            )}
          </label>
        </FilterSection>

        {/* Room */}
        <FilterSection title="Room">
          <div className="flex flex-wrap gap-2">
            {ROOMS.map((r) => (
              <Pill key={r} active={draft.room === r} onClick={() => setRoom(r)}>
                {r}
              </Pill>
            ))}
          </div>
        </FilterSection>

        {/* Distance */}
        <FilterSection
          title="Distance"
          aside={
            <span className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              {draft.distanceKm} km
            </span>
          }
        >
          <input
            type="range"
            min={1}
            max={25}
            step={1}
            value={draft.distanceKm}
            onChange={(e) => setDistance(Number(e.target.value))}
            className={cn(
              'h-1.5 w-full appearance-none bg-white/10',
              '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
              '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-pink',
              '[&::-webkit-slider-thumb]:shadow-glow-soft',
              '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4',
              '[&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-pink',
            )}
          />
          <div className="mt-2 flex justify-between font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
            <span>1 km</span>
            <span>25 km</span>
          </div>
        </FilterSection>

        {/* Presence */}
        <FilterSection title="Presence">
          <div className="grid grid-cols-3 gap-1.5">
            {PRESENCES.map((p) => (
              <SegmentButton
                key={p}
                active={draft.presence === p}
                onClick={() => setPresence(p)}
              >
                {p}
              </SegmentButton>
            ))}
          </div>
        </FilterSection>

        {/* HEAT band */}
        <FilterSection
          title="Heat"
          aside={
            <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              {draft.bands.length} of 4
            </span>
          }
        >
          <div className="grid grid-cols-4 gap-1.5">
            {BANDS.map((b) => (
              <SegmentButton
                key={b}
                active={draft.bands.includes(b)}
                onClick={() => toggleBand(b)}
              >
                {b}
              </SegmentButton>
            ))}
          </div>
        </FilterSection>
      </div>
    </Sheet>
  );
};

const FilterSection = ({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section>
    <header className="mb-3 flex items-center justify-between">
      <span className="inline-flex items-center gap-3 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        <span aria-hidden className="h-px w-5 bg-text-muted/60" />
        {title}
      </span>
      {aside}
    </header>
    {children}
  </section>
);

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'border px-3 py-1.5 font-display text-[10px] font-bold uppercase tracking-widest',
      'transition-[border-color,color,background] duration-fast ease-signal',
      active
        ? 'border-pink bg-pink/10 text-pink shadow-glow-soft'
        : 'border-white/15 text-text-secondary hover:border-pink/40 hover:text-text-primary',
    )}
  >
    {children}
  </button>
);

const SegmentButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'border px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest',
      'transition-[border-color,color,background] duration-fast ease-signal',
      active
        ? 'border-pink bg-pink/10 text-pink shadow-glow-soft'
        : 'border-white/15 text-text-secondary hover:border-pink/40',
    )}
  >
    {children}
  </button>
);
